/**
 * VRG-545 — THE CONTRACT WITH vera-website, in one place.
 *
 * Every field the marketing site collects and forwards, declared once. The
 * signups grid builds its columns from this; a drift guard checks it against
 * the website's own source.
 *
 * ── Why a manifest and not just columns ──────────────────────────────────────
 *
 * These fields are authored in ANOTHER REPO on its own release cadence, and the
 * failure mode is not a crash — it is silence. A field the site stops sending
 * renders an em-dash, which is indistinguishable from a visitor who left it
 * blank. A field the site ADDS lands in no column at all. Both look like
 * nothing happening, which is why three separate gaps (`cac`,
 * `resaleBuyerConversion`, `certificateLeadYield`) survived a review that was
 * looking for exactly them.
 *
 * A list of columns cannot be compared to anything. A manifest can:
 * `signups-website-field-coverage.guard.spec.ts` reads vera-website's own
 * `content/roi.ts` when that repo is present and fails on any divergence.
 *
 * ⚠️ THE GUARD'S HONEST LIMIT: the website repo is not available in CI, so there
 * the guard can only check the weaker half — that every field here has a column.
 * The half that catches real drift runs on a developer machine with both repos
 * checked out. That is a genuine gap, not one papered over: the STRONG check
 * belongs in vera-website (a test that it forwards everything it collects),
 * because from this side a field the site never sends is indistinguishable from
 * one the visitor left blank.
 *
 * See working-conventions.md §76.
 */

/** Where the value arrives from, and therefore which submission blob holds it. */
export type WebsiteFieldGroup = 'roi' | 'waitlist'

export type WebsiteFieldKind =
  /** A count. Rendered with grouping separators, no unit. */
  | 'number'
  /**
   * Money, rendered with the symbol for `roi_submission.currency`.
   *
   * ⚠️ THE SYMBOL COMES FROM THE ROW, NEVER FROM A LITERAL. vera-website ships a
   * currency selector (`currencies` / `DEFAULT_CURRENCY` in its `content/roi.ts`) and
   * forwards the chosen ISO 4217 code, so a `$` hardcoded here becomes a lie the first
   * time a euro submission arrives. See `currencySymbolOf` in `websiteValues.ts`, which
   * also carries the USD fallback and the reason it is correct rather than lazy.
   *
   * VRG-577 is COMPANY-LEVEL CONVERSION, not the selector — nothing here converts, and
   * a figure is only ever displayed in the unit the visitor typed it in.
   */
  | 'currency'
  /** Stored as a 0–1 ratio, displayed as a percent. See `assumptionsUnit`. */
  | 'rate'
  /** A list of labels or slugs. */
  | 'list'
  /** A single string. */
  | 'text'

export interface WebsiteField {
  /** The key as it arrives on the wire. */
  id: string
  /** Column header AND panel label. Matches the website's own wording. */
  label: string
  group: WebsiteFieldGroup
  kind: WebsiteFieldKind
  /**
   * Nested under `assumptions` rather than at the top level of the blob.
   * The website sends its five—now seven—sliders as one record.
   */
  nested?: 'assumptions'
  /**
   * The website's seeded default, for the "is this their answer or ours?"
   * signal. `null` where the field has no default (the visitor supplies it or
   * leaves it blank).
   *
   * ⚠️ Rates here are RATIOS, matching how they are stored — the website
   * declares them as percents and the API converts on ingress.
   */
  websiteDefault: number | null
  /**
   * Grid column width. Sized for the HEADER, never the values (§49).
   *
   * ⚠️ MEASURED IN THE BROWSER, NOT ESTIMATED FROM CHARACTER COUNT. The widths below were
   * first derived from the guard's own `label.length * 7 + 50` rule of thumb, and two of
   * them were WRONG in the direction that matters: `Counterfeit exposure` rendered as
   * `Counterfeit exposur…` and `Resale buyers who buy new` clipped too, because a
   * proportional font does not average 7px a character and the grid reserves **63px** per
   * header for its sort icon, menu affordance and cell padding before the label gets any.
   *
   * The formula that actually holds, measured at 14px Lato on `/internal/signups`:
   *
   *     width = ceil(textWidth) + 63 (grid chrome) + 8 (slack)
   *
   * and a column whose header also carries an info glyph adds `INFO_HEADER_ALLOWANCE` (18)
   * ON TOP — that allowance lives in `websiteColumns.tsx` rather than here, because the
   * tooltip is a rendering decision and would be wrong for any column that later drops it.
   *
   * The guard's rule of thumb is kept as a FLOOR, not a target: it is cheap, it runs in CI
   * where no browser does, and it catches the gross case (a number column given 100px
   * because its values are short). It is not a substitute for looking.
   */
  width: number
  /**
   * This field is RENDERED BY ANOTHER FIELD'S COLUMN, and gets none of its own.
   *
   * The one live case is `industries` → `categories`: two independent submissions asking
   * the same question in two vocabularies (the ROI form sends category SLUGS, the waitlist
   * form sends display LABELS), which as two columns put `handbags` beside
   * `handbags, leather-go…` on one row and read as a contradiction.
   *
   * ⚠️ THE FIELD STAYS IN THE MANIFEST, and that is the entire reason this is a flag
   * rather than a deletion. Deleting `industries` would silence the drift guard for it —
   * a rename or a removal on vera-website would then land in no column AND trip no check,
   * which is the exact silence `websiteFields.ts` exists to break. Declared-and-merged is
   * a different claim from absent, and only one of them is true.
   *
   * The target must itself be a real, unmerged field, asserted by
   * `signups-website-field-coverage.guard.spec.ts` — a merge pointing at nothing would
   * drop the field from the grid with no error.
   */
  mergedInto?: string
}

/**
 * ── `cac` IS FORWARDED. Corrected 2026-09-07 (VRG-545) ───────────────────────────────
 *
 * This docblock previously said, in capitals, that `cac` IS NOT FORWARDED, and the grid
 * carried a header tooltip telling reviewers a blank in that column might mean "the site
 * never sent it". Both were wrong, and wrong in the direction that costs something: they
 * taught a reviewer to discount a real, answered figure on a lead-qualification screen.
 *
 * Re-read at vera-website's `app/api/roi/route.ts`: the `forwardRoi({...})` call lists
 * `email`, `units`, `price`, `cac`, `total`, `currency`, `assumptions`,
 * `assumptionsTouched`, `industries` and `source`. `cac` is the fourth entry and carries
 * its own comment about sending `null` rather than 0 for an unanswered field.
 *
 * ⚠️ SO A BLANK HERE MEANS THE VISITOR LEFT IT BLANK, which is a real and expected answer:
 * the website defaults CAC to 0 precisely so the field renders empty behind a placeholder,
 * and the route collapses that 0 to `null` on the way out rather than storing a fabricated
 * cost. "Not entered" is the only reading; "not sent" is no longer one.
 *
 * ── The lesson the previous version got RIGHT, kept ───────────────────────────────────
 *
 * That docblock was once softened to "MAY NOT BE FORWARDED" on the evidence that the dev
 * database's row carries `cac: 140` — a row hand-written by a `curl`, so evidence about a
 * test payload and not about the website. Correctly rejected at the time. The point stands
 * and is why this correction cites the other repo's `forwardRoi` call rather than the same
 * dev row: seeded data is not a witness to a contract, in either direction.
 *
 * ⚠️ AND IT IS WHY THIS FILE IS NOT SELF-CORRECTING. Nothing in vera-2 could have noticed
 * the forward gaining a field — the strong half of the guard only reads vera-website's
 * `content/roi.ts` sliders, not its route's payload — so this claim went stale silently and
 * was still being read as current. The check that would have caught it belongs over there
 * (§76: "we forward everything we collect"), and until it exists, a prose claim in this
 * file about what the site sends is only as fresh as the last person who opened both repos.
 */
export const WEBSITE_FIELDS: readonly WebsiteField[] = [
  // ── ROI calculator: the visitor's own figures ──────────────────────────────
  { id: 'units', label: 'Units per year', group: 'roi', kind: 'number', websiteDefault: 25000, width: 160 },
  { id: 'price', label: 'Average retail price', group: 'roi', kind: 'currency', websiteDefault: 850, width: 190 },
  { id: 'cac', label: 'Customer acquisition cost', group: 'roi', kind: 'currency', websiteDefault: 0, width: 230 },
  /*
    "ROI calculator total", not "Calculator total": a bare "Calculator" leaves a reviewer to
    infer WHICH submission the figure came from, and this grid has a waitlist form sitting
    beside the calculator.

    Width from this file's own measured formula, not from the guard's floor: the label
    renders at 120px (measured in the browser at 14px Lato, not estimated), so
    120 + 63 grid chrome + 8 slack = 191. The column builder adds INFO_HEADER_ALLOWANCE on
    top because this column carries a tooltip, giving 209 rendered. Comfortably above the
    guard's `20 * 7 + 50 = 190` floor, which is a floor and not the target.
  */
  { id: 'total', label: 'ROI calculator total', group: 'roi', kind: 'currency', websiteDefault: null, width: 191 },

  // ── ROI calculator: the seven assumption sliders ───────────────────────────
  // Labels are the website's own `label` strings, so a reviewer reads the words
  // the visitor answered rather than a paraphrase.
  { id: 'counterfeitRate', label: 'Counterfeit exposure', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.023, width: 205 },
  { id: 'resaleRate', label: 'Units resold at least once', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.2, width: 235 },
  { id: 'resaleValueRatio', label: 'Resale price vs retail', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.45, width: 205 },
  { id: 'brokerTake', label: 'Your share of a resale', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.05, width: 205 },
  { id: 'returnFraudRate', label: 'Returns that are fraudulent', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.015, width: 240 },
  /*
    ⚠️ 0.28, not 0.05 — corrected 2026-09-07 (VRG-545) after running this guard's STRONG
    half with vera-website checked out beside the repo, which reported the drift by name:
    the site declares `value: 28` (`content/roi.ts`) and this said 5.

    Worth recording HOW it was missed rather than just fixing it. The strong half does not
    run in CI, so for as long as nobody ran it locally the manifest carried a default that
    was wrong by a factor of five, and the only place it surfaced was a header tooltip
    telling a reviewer "the site starts this at 5%" — a plausible sentence, about the wrong
    number, on a screen whose entire job is separating a visitor's answer from ours. Exactly
    the silent-not-crashing failure this file's header describes, landing on this file.
  */
  { id: 'resaleBuyerConversion', label: 'Resale buyers who buy new', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.28, width: 240 },
  { id: 'certificateLeadYield', label: 'New customers from certificates', group: 'roi', kind: 'rate', nested: 'assumptions', websiteDefault: 0.01, width: 270 },

  /*
    Category SLUGS on this side; the waitlist form's `categories` carries LABELS.

    MERGED into `categories` — one question, asked twice, in two vocabularies. As two
    columns it rendered `handbags` next to `handbags, leather-go…` on the same row, which
    reads as the two forms contradicting each other rather than agreeing.

    ⚠️ The merge is a UNION OVER A NORMALIZED KEY, not a concatenation: the two submissions
    are genuinely independent (separate POSTs, separate parses), so a category picked on
    both forms arrives twice and would be counted twice. See `websiteCategories.ts`.

    `width` is unused while merged and is kept only because the guard's floor still applies
    to every manifest entry — an un-merged field must never inherit a nonsense width from a
    period when nothing rendered it.
  */
  { id: 'industries', label: 'Modelled categories', group: 'roi', kind: 'list', websiteDefault: null, width: 200, mergedInto: 'categories' },

  // ── Waitlist form ─────────────────────────────────────────────────────────
  // Deliberately NOT prefixed `waitlist_form.`. A waitlist submission and an ROI
  // submission are both company information arriving before the company exists;
  // prefixing enshrines the accident of which form carried it. These become real
  // company attributes at grant time — see the task file's Part 4.
  /*
    THE MERGE TARGET — renders its own `categories` plus the ROI form's `industries`.

    Sized for CONTENT here rather than for the header, which is the one deliberate
    departure from §49 in this manifest and is worth naming: the cell renders icon CHIPS,
    not text, so the header floor (176px) would clip the second chip. The header is not at
    risk — 176 < this — so §49's actual requirement (never truncate a header) still holds;
    what is relaxed is only the "size for the header, NOT the values" guidance, because a
    chip is a value whose width nothing else bounds.
  */
  { id: 'categories', label: 'Product categories', group: 'waitlist', kind: 'list', websiteDefault: null, width: 340 },
  { id: 'solutions', label: 'Interested in', group: 'waitlist', kind: 'list', websiteDefault: null, width: 170 },
  { id: 'planInterest', label: 'Plan interest', group: 'waitlist', kind: 'text', websiteDefault: null, width: 150 },
]

/** The assumption fields, in order — the ones the default/changed signal applies to. */
/**
 * VRG-651 — the two ATTRIBUTION fields the waitlist forward carries are deliberately NOT in
 * WEBSITE_FIELDS: they are not answers stored in `waitlist_submission`, they are consumed by
 * the API as NAMED fields and become `referrals` rows (see `ReferralAttributionService`).
 * The grid shows them as the row's `referral` ("Referred by", VRG-658), not as columns.
 *
 *   referral_code  vera.inc/refer/<code>, shape /^[A-Za-z0-9_-]{3,64}$/ (website-12
 *                  lib/referral-code.ts). Unknown codes are ignored silently.
 *   insider_ref    FirstPromoter's `_fprom_ref`, shape /^[A-Za-z0-9_.-]{1,64}$/. Present only
 *                  with analytics consent.
 *
 * Snake_case on the wire, unlike every field above (the contract the website shipped).
 * Removing or renaming either owes vera-website the same change (§76).
 *
 *   firstTouch     VRG-728 — the visit's campaign tags, referrer HOSTNAME and landing PATH
 *                  (vera-website lib/attribution.ts), sent by BOTH forms. Declared as
 *                  `FirstTouchBodyDto`, stored in typed `signups` columns once (never
 *                  overwritten), shown as the grid's "First touch" column and fed into the
 *                  sales pipeline's lead source. camelCase on the wire, like the fields above it.
 */
export const WEBSITE_ATTRIBUTION_FIELDS = ['referral_code', 'insider_ref', 'firstTouch'] as const

export const ASSUMPTION_FIELDS = WEBSITE_FIELDS.filter(f => f.nested === 'assumptions')

/**
 * EVERY FIELD THE ROI CALCULATOR ASKS THE VISITOR TO FILL IN — the denominator of the
 * "N/10" count on the grid's ROI column. Ten: `units`, `price`, `cac`, and the seven
 * assumption sliders.
 *
 * ## Derived from `websiteDefault !== null`, not from a hand-written list of ten ids
 *
 * A literal list would be an eleventh place to update when vera-website adds a slider, and
 * this file exists precisely because a field added over there must not need a second edit
 * over here to be noticed. The rule picks out the same set for a reason that is semantic
 * rather than convenient: a `websiteDefault` IS the site seeding a control it then invites
 * the visitor to change. `total` is the calculator's OUTPUT (no default, nobody types it)
 * and `industries` is a picker with no seeded value, so both fall out correctly.
 *
 * ⚠️ AND THE RULE IS NOT MERELY A PROXY — IT IS THE EXACT SET THE COUNT CAN SPEAK ABOUT.
 * "Did the visitor supply this?" is answered by `websiteDefaultState`, which needs either a
 * recorded `assumptionsTouched` entry or a default to compare against. A hypothetical ROI
 * input with neither would be uncountable by construction, so including it would inflate
 * the denominator with a field that can never be counted in the numerator — a pill that
 * silently caps at 10/11. Excluding it is the honest answer, not an oversight.
 *
 * The count is asserted at exactly ten, with its members named, by
 * `signups-website-field-coverage.guard.spec.ts`. That assertion is what turns "the rule
 * happens to select the right set today" into something that fails loudly if it stops.
 */
export const ROI_INPUT_FIELDS = WEBSITE_FIELDS.filter(
  f => f.group === 'roi' && f.websiteDefault !== null,
)
